import { Component, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { CheckboxChangeEvent, HcPopComponent, HcTableDataSource, PaginationComponent } from '@healthcatalyst/cashmere';
import { GridBulkActionsComponent } from '../grid-bulk-actions/grid-bulk-actions.component';
import { GridBaseComponent } from '../grid-base.component';
import { generateStringToSearch } from '../../models/grid';
import { stringSearch } from '../../utils/string.utils';
import { SelectableJob, ValidatableSelectableJob, defaultSelectJobGridColumns } from './select-job.models';
import { Observable, finalize } from 'rxjs';
import { TenantIngestionJobService } from 'src/app/core/services/application/tenant-ingestion-job.service';
import { ToastNotificationService } from 'src/app/core/services/toast-notification.service';
import { FormControl, FormGroup } from '@angular/forms';
import { JobSelectValidationMode, SelectJobsValidateService } from './select-jobs-validate.service';
import { rawModes } from '../../constants/constants';
import { TenantIngestionPriorityType } from 'src/app/core/models/tenant-ingestion-priority-type';

type SelectJobsGridFilters = { term: string; filterToOnlyActiveJobs: boolean; ingestMode: string };
@Component({
    selector: 'cdp-select-jobs-grid',
    templateUrl: './select-jobs-grid.component.html',
    styleUrls: ['./select-jobs-grid.component.scss'],
    standalone: false
})
export class SelectJobsGridComponent extends GridBaseComponent<ValidatableSelectableJob> implements OnInit {
    @Input() public set jobs(jobs: SelectableJob[]) {
        this.handleJobsInputChanged(jobs);
    }
    @Input() public loading: boolean;
    @Input() public loadingError: boolean;
    @Input() public settingsStorageKey: string;
    @Input() public nameSingular = 'job';
    @Input() public namePlural = 'jobs';
    @Input() public emptyMsg = 'No jobs to show';
    @Input() public tenantId: string;
    @Input() public allowInactiveJobSelection = true;
    @Input() public allowJobStateTogglePerRow = true;
    @Input() public allowJobStateFilter = true;
    /** Determine how to validate the job selected */
    @Input() public validationMode: 'none' | 'onePerEntity' | 'onePerIngestMode' = 'none';
    @Input() public showToolbar = true;
    /** Array of strings (property names) representing which columns to show by default. See `defaultSelectJobGridColumns` for options. */
    @Input() public set colsToShowByDefault(cols: string[]) {
        this.setupCols(cols);
    }
    @Output() public refreshRequested: EventEmitter<void> = new EventEmitter<void>();
    @Output() public jobStatusChanged: EventEmitter<number> = new EventEmitter<number>();
    @ViewChild('paginator', { static: true }) public paginator: PaginationComponent;
    @ViewChild('modeOptionsPop') modeOptionsPop: HcPopComponent;

    @ViewChild(GridBulkActionsComponent, { static: true }) public bulkActions: GridBulkActionsComponent<SelectableJob>;

    protected override gridSettingsStorageKey: string;
    public staticPrefixColumns = ['checkbox'];
    public staticSuffixColumns = ['info'];
    rawModeOptions = Object.values(rawModes);

    public get selectionIsValid(): boolean {
        return this.selectJobsValidateService.selectionIsValid;
    }
    public jobChangeInProgress: boolean;
    public get selectedJobs(): SelectableJob[] {
        return this.bulkActions.selectedRows;
    }
    public get hiddenSelectionsCount(): number {
        return this.bulkActions.selectedRowsHidden;
    }
    public gridCols = defaultSelectJobGridColumns;
    public entityCount = 1;
    infoPopItem: ValidatableSelectableJob;
    searchTerm: string;
    selectJobsFilterForm: FormGroup = new FormGroup({
        filterToOnlyActiveJobs: new FormControl(false),
        ingestMode: new FormControl('')
    });

    constructor(
        private ingestionJobService: TenantIngestionJobService,
        private selectJobsValidateService: SelectJobsValidateService,
        private toast: ToastNotificationService
    ) {
        super(undefined);
    }

    public ngOnInit(): void {
        if (!this.settingsStorageKey) {
            throw new Error('settingsStorageKey Input property is not set to a truthy value.');
        }

        this.gridSettingsStorageKey = this.settingsStorageKey;
        this.dataSource = new HcTableDataSource<ValidatableSelectableJob>([]);
        this.dataSource.paginator = this.paginator;

        this.dataSource.filterPredicate = this.filterPredicate.bind(this);
        this.wireUpDataGridSettingsCache();
        this.selectJobsFilterForm.valueChanges.pipe(this.unsubscribeOnDestroy()).subscribe(() => {
            this.filter();
        });
    }

    selectMode(mode: string): void {
        this.selectJobsFilterForm.controls['ingestMode'].setValue(mode);
        this.modeOptionsPop.close();
    }

    public onMasterCheckboxClicked(event: CheckboxChangeEvent): void {
        this.bulkActions.onMasterCheckboxClicked(event);
        this.selectJobsValidateService.refreshValidity(this.dataSource.data, this.validationModeEnum);
    }

    private get validationModeEnum(): JobSelectValidationMode {
        switch (this.validationMode) {
            case 'onePerEntity':
                return JobSelectValidationMode.OnePerEntity;
            case 'onePerIngestMode':
                return JobSelectValidationMode.OneActivePerIngestMode;
            case 'none':
            default:
                return JobSelectValidationMode.None;
        }
    }

    public toggleSelectItem(event: CheckboxChangeEvent, job: ValidatableSelectableJob): void {
        this.bulkActions.toggleSelectItem(event, job);
        this.selectJobsValidateService.refreshValidity(this.dataSource.data, this.validationModeEnum);
    }

    public isRowClickable(job: ValidatableSelectableJob): boolean {
        return !job.unselectable && !this.jobChangeInProgress && !(!this.allowInactiveJobSelection && !job.activeFlag);
    }

    public onRowClick(job: ValidatableSelectableJob): void {
        if (this.isRowClickable(job)) {
            job.isSelected = !job.isSelected;
            this.selectJobsValidateService.refreshValidity(this.dataSource.data, this.validationModeEnum);
        } else if (job.isSelected) {
            // we'll always allow de-selecting a selected job, even if the row is otherwise disabled
            job.isSelected = false;
        }
    }

    public onActiveFlagToggle(job: ValidatableSelectableJob): void {
        if (!job.ingestionId) {
            return;
        }

        const newActiveState = !job.activeFlag;
        job.activeFlag = newActiveState;
        if (!this.allowInactiveJobSelection) {
            job.unselectable = !job.activeFlag;
            if (job.isSelected && !job.activeFlag) {
                job.isSelected = false; // De-select jobs about to be de-activated
                this.selectJobsValidateService.refreshValidity(this.dataSource.data, this.validationModeEnum);
            }
        }

        this.toggleJobActiveState(job);
    }

    private toggleJobActiveState(job: ValidatableSelectableJob): void {
        if (!job.ingestionId) {
            return;
        }
        this.jobChangeInProgress = true;
        let changeRequest: Observable<object>;

        if (job.activeFlag) {
            changeRequest = this.ingestionJobService.activateTenantJobs(this.tenantId, [job.ingestionId]);
        } else {
            changeRequest = this.ingestionJobService.deactivateTenantJobs(this.tenantId, [job.ingestionId]);
        }

        this.handleActiveJobChangeRequest(job, changeRequest);
    }

    public handleActiveJobChangeRequest(job: ValidatableSelectableJob, changeRequest: Observable<object>): void {
        changeRequest
            .pipe(
                finalize(() => {
                    this.jobChangeInProgress = false;
                }),
                this.unsubscribeOnDestroy()
            )
            .subscribe({
                next: () => {
                    const header = `${job.activeFlag ? 'Activated' : 'Deactivated'} Job `;
                    const msg = `Successfully ${job.activeFlag ? 'activated' : 'deactivated'} job for entity '${job.entityName}'.`;
                    this.toast.showSuccess(msg, header, { timeout: 1000 });
                    this.jobStatusChanged.emit(job.ingestionId || 0);
                    this.selectJobsValidateService.refreshValidity(this.dataSource.data, this.validationModeEnum);
                },
                error: e => {
                    let message = `Unable to ${job.activeFlag ? 'activate' : 'deactivate'} job.`;
                    message += e.status != 500 ? e.error.message : 'Contact an admin if the problem continues.';
                    this.toast.showError(message, `Update Failed`);
                    // Reverting job back to state before request
                    job.activeFlag = !job.activeFlag;
                    if (!this.allowInactiveJobSelection) {
                        job.unselectable = !job.activeFlag;
                    }
                }
            });
    }

    private handleJobsInputChanged(jobs: SelectableJob[]): void {
        if (!jobs) {
            return;
        }

        const jobsUpdatedForPriority = jobs.map(job => ({
            ...job,
            priority: this.setPriorityDisplayValue(job.priority)
        }));

        const mappedJobs = jobsUpdatedForPriority.map(j => ({ ...j, valid: true }));
        this.dataSource.data = mappedJobs;
        this.entityCount = new Set<number>(jobs.map(j => <number>j.entityId)).size;
        this.selectJobsValidateService.refreshValidity(this.dataSource.data, this.validationModeEnum);
    }

    public search(term: string): void {
        this.searchTerm = term;
        this.filter();
    }

    public filter() {
        this.dataSource.filter = JSON.stringify({
            term: this.searchTerm || '',
            filterToOnlyActiveJobs: this.selectJobsFilterForm.value.filterToOnlyActiveJobs,
            ingestMode: this.selectJobsFilterForm.value.ingestMode
        });
    }

    private filterPredicate(data: SelectableJob, filter: string): boolean {
        const filterObj = JSON.parse(filter) as SelectJobsGridFilters;
        if (filterObj.filterToOnlyActiveJobs && !data.activeFlag) {
            return false;
        }

        if (filterObj.ingestMode && filterObj.ingestMode !== data.ingestMode) {
            return false;
        }

        const stringToSearch = generateStringToSearch(data, this.gridCols);
        return stringSearch(stringToSearch, filterObj.term);
    }

    private setupCols(cols: string[]) {
        this.gridCols.forEach(col => {
            col.isShownByDefault = false;
        });

        cols.forEach(col => {
            const colToShow = this.gridCols.find(c => c.name === col);
            if (colToShow) {
                colToShow.isShownByDefault = true;
            }
        });
    }

    private setPriorityDisplayValue(priority: string | null): string {
        return priority === TenantIngestionPriorityType.High ? 'HP' : '-';
    }
}
